import { Injectable } from '@angular/core'
import { AuthConfig, OAuthService, UserInfo } from 'angular-oauth2-oidc'
import { BehaviorSubject, firstValueFrom } from 'rxjs'
import { filter, take } from 'rxjs/operators'
import { authConfigACC, authConfigWeb } from '../../environments/environment'
import { AuthenticationRepository } from '../repositories/authentication-repository.service'
import { UserRepository } from '../repositories/user.repository'
import { Router } from '@angular/router'
import { WidgetService } from './widget.service'
import { DeviceModeRepository } from '../repositories/device-mode.repository'

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public mailAdress = ''
  private authConfig: AuthConfig

  private initialized = new BehaviorSubject<boolean>(false)
  public readonly waitUntilInitialized = firstValueFrom(
    this.initialized.pipe(
      filter((x) => x),
      take(1)
    )
  )
  private readonly profileUrl: string
  private readonly signUpUrl: string

  constructor(
    private readonly router: Router,
    private readonly oauthService: OAuthService,
    private readonly userRepository: UserRepository,
    private readonly authenticationRepository: AuthenticationRepository,
    private readonly widgetService: WidgetService,
    private readonly deviceModeRepository: DeviceModeRepository
  ) {
    this.authConfig = this.widgetService.isOnWidgetHostname ? authConfigACC : authConfigWeb
    this.authConfig.redirectUri = window.location.origin + '/login'

    this.profileUrl = `${this.authConfig.issuer}`
    this.signUpUrl = `${this.authConfig.issuer}/Account/Register`
  }

  public async register(): Promise<void> {
    if (this.widgetService.isOnWidgetHostname) {
      window.open(this.signUpUrl, '_blank')
    } else {
      location.href = this.signUpUrl
    }
  }

  public login(): void {
    if (this.isOAuthLoggedIn) {
      this.authenticationRepository.authenticated$.next(true)
      void this.router.navigate(['/homepage'])
    } else {
      this.oauthService.initLoginFlow()
    }
  }

  public async logoutAndRedirectToLogin(): Promise<void> {
    return this.authenticationRepository.logoutAndRedirectToLogin()
  }

  public async configureOauth2(): Promise<void> {
    this.oauthService.configure(this.authConfig)
    this.oauthService.timeoutFactor = 0.5
    this.oauthService.setupAutomaticSilentRefresh(undefined, 'access_token')

    try {
      await this.oauthService.loadDiscoveryDocumentAndTryLogin()

      if (!this.isOAuthLoggedIn && this.oauthService.getRefreshToken()?.length > 0) {
        await this.oauthService.refreshToken()
      }

      this.setMailAdress()
      await this.updateAuthentication(this.isOAuthLoggedIn)
    } catch (err: unknown) {
      console.error(err)
    }

    this.initialized.next(true)
  }

  get isOAuthLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken()
  }

  public getAccessToken(): string | null {
    return this.oauthService.getAccessToken()
  }

  public async isDokaUser(): Promise<boolean> {
    try {
      // method crashes if we have a trial user (no user - access token)
      const profile = (await this.oauthService.loadUserProfile()) as UserInfo
      const isDokaUser = profile.info.email.indexOf('@doka.com') !== -1

      return isDokaUser
    } catch {
      return false
    }
  }

  public async refreshToken(): Promise<void> {
    if (!this.oauthService.hasValidAccessToken()) {
      this.oauthService.timeoutFactor = 0.5
      this.oauthService.setupAutomaticSilentRefresh(undefined, 'access_token')
      await this.oauthService.refreshToken()
    }
  }

  public async openProfilManagement(): Promise<void> {
    location.href = this.profileUrl
  }

  private setMailAdress(): void {
    if (this.isOAuthLoggedIn) {
      void this.oauthService.loadUserProfile().then((val) => {
        const profile = val as UserInfo
        this.mailAdress = profile.info.email
      })
    }
  }

  public async updateAuthentication(isLoggedIn: boolean): Promise<void> {
    if (isLoggedIn) {
      await this.userRepository.bootstrapCurrentUser()
    }
    this.authenticationRepository.updateAuthentication(isLoggedIn)
  }

  public async getUserName(): Promise<string> {
    const userInfo = (await this.oauthService.loadUserProfile()) as UserInfo
    return userInfo.info.name
  }

  public async loadUserInfo(): Promise<void> {
    const userInfo = (await this.oauthService.loadUserProfile()) as UserInfo
    this.userRepository.updateUserInfo(userInfo)
  }
}
